import axios from 'axios'
import { useAuthStore } from '@/features/auth/store'
import { API_URL } from '@/shared/constants/config'

// Global flag to prevent concurrent refresh attempts
let isRefreshing = false
let refreshPromise: Promise<string | null> | null = null

/**
 * Refreshes the access token using the refresh token
 * Ensures only one refresh happens at a time by using a shared promise
 */
export async function refreshAccessToken(): Promise<string | null> {
  // Return existing refresh promise if already refreshing
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  // Set refreshing flag and create new promise
  isRefreshing = true
  refreshPromise = performTokenRefresh()

  try {
    const result = await refreshPromise
    return result
  } finally {
    isRefreshing = false
    refreshPromise = null
  }
}

async function performTokenRefresh(): Promise<string | null> {
  const authStore = useAuthStore.getState()
  const refreshToken = authStore.refreshToken

  if (!refreshToken) {
    authStore.clearAuth()
    return null
  }

  try {
    const response = await axios.post(
      `${API_URL}/auth/refresh`,
      {},
      {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
          'Content-Type': 'application/json',
        },
      }
    )

    const { accessToken, refreshToken: newRefreshToken } = response.data

    // Update tokens in store
    authStore.updateTokens(accessToken, newRefreshToken || refreshToken)

    return accessToken
  } catch (error) {
    authStore.clearAuth()
    throw error
  }
}

/**
 * Checks if the access token is expiring soon and refreshes if needed
 */
export async function ensureValidToken(): Promise<string | null> {
  const authStore = useAuthStore.getState()
  const accessToken = authStore.accessToken

  if (!accessToken) {
    return null
  }

  // Check if token is expiring soon (within 1 minute)
  if (authStore.isTokenExpiringSoon(accessToken, 60)) {
    return await refreshAccessToken()
  }

  return accessToken
}
